import {
  Paper,
  Group,
  Stack,
  Text,
  Badge,
  Menu,
  Modal,
  Button,
} from '@mantine/core';
import {
  IconFileText,
  IconAlertTriangle,
  IconCircleCheck,
  IconMenu2,
} from '@tabler/icons-react';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useTimeAgo } from '@commons/timeAgo';
import { getStatus } from '@commons/getStatus';
import { File as FileType } from '../../types';

export const File = ({
  file,
  onPreview,
  onEdit,
  onConfirm,
}: {
  file: FileType;
  onPreview: () => void;
  onEdit: () => void;
  onConfirm: () => void;
}) => {
  const { isLocked, isVerified, updatedAt, status, name } = file;

  const timeAgo = useTimeAgo();
  const intl = useIntl();
  const [confirmationDialogOpened, setConfirmationDialogOpened] =
    useState(false);

  const statusInfo = getStatus(status);

  const getBadgeColor = (variant: string) => {
    switch (variant) {
      case 'positive':
        return 'green';
      case 'notice':
        return 'yellow';
      case 'negative':
        return 'red';
      case 'neutral':
        return 'gray';
      case 'informative':
      default:
        return 'blue';
    }
  };

  return (
    <Paper withBorder p="md" radius="md">
      <Stack gap="xs">
        <Group justify="space-between" align="center" wrap="nowrap">
          <Stack gap="xs" style={{ minWidth: 0, flex: 1 }}>
            <Group gap="xs" align="center" wrap="nowrap">
              <IconFileText
                size={20}
                color={isLocked ? 'gray' : undefined}
                style={{ flexShrink: 0 }}
              />
              <Text fz="sm" c={isLocked ? 'dimmed' : undefined}>
                {name}.pdf
              </Text>
            </Group>
          </Stack>

          <Group
            gap="xs"
            align="center"
            wrap="nowrap"
            style={{ flexShrink: 0 }}
          >
            {!isVerified && status >= 1 && status < 2 && !isLocked && (
              <>
                <IconAlertTriangle
                  size={20}
                  color="orange"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setConfirmationDialogOpened(true)}
                />
                <Modal
                  opened={confirmationDialogOpened}
                  onClose={() => setConfirmationDialogOpened(false)}
                  title={intl.formatMessage({ id: 'attention' })}
                  centered
                >
                  <Text size="sm">
                    {intl.formatMessage({ id: 'needsConfirmation' })}
                  </Text>
                  <Group justify="flex-end" mt="md">
                    <Button
                      variant="subtle"
                      onClick={() => setConfirmationDialogOpened(false)}
                    >
                      {intl.formatMessage({ id: 'back' })}
                    </Button>
                  </Group>
                </Modal>
              </>
            )}

            {isLocked && <IconCircleCheck size={24} color="green" />}

            <Menu
              withinPortal
              position="left"
              trigger="click"
              withArrow
              closeOnItemClick={true}
              transitionProps={{ exitDuration: 0 }}
            >
              <Menu.Target>
                <Button size="xs" variant="subtle" p={5} color="gray">
                  <IconMenu2 size={20} />
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                {file.status >= 1 && (
                  <Menu.Item
                    onClick={(e) => {
                      e.stopPropagation();
                      onPreview();
                    }}
                  >
                    {intl.formatMessage({ id: 'preview' })}
                  </Menu.Item>
                )}

                {!file.isSigned && file.status >= 1 && !file.isLocked && (
                  <Menu.Item
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit();
                    }}
                  >
                    {intl.formatMessage({ id: 'edit' })}
                  </Menu.Item>
                )}

                {!file.isVerified &&
                  !file.isLocked &&
                  file.status >= 1 &&
                  file.status < 2 && (
                    <Menu.Item
                      onClick={(e) => {
                        e.stopPropagation();
                        onConfirm();
                      }}
                    >
                      {intl.formatMessage({ id: 'confirm' })}
                    </Menu.Item>
                  )}

                {!file.isSigned &&
                  !file.isLocked &&
                  !file.isVerified &&
                  file.status === 0 && (
                    <Menu.Item
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit();
                      }}
                    >
                      {intl.formatMessage({ id: 'complete' })}
                    </Menu.Item>
                  )}
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
        <Group gap="xs" align="center" wrap="nowrap">
          <Badge
            size="sm"
            color={getBadgeColor(statusInfo?.variant || 'informative')}
            variant="dot"
          >
            {intl.formatMessage({ id: statusInfo?.label })}
          </Badge>

          <Text size="xs" c="dimmed">
            {intl.formatMessage({ id: 'updated' })}{' '}
            {timeAgo.format(new Date(updatedAt))}
          </Text>
        </Group>
      </Stack>
    </Paper>
  );
};
